import type {
  StoreTransitionMeetingDto,
  TransitionMeetingFormData,
} from "meetings/advisory/types";
import {
  addOneWeekToDate,
  fromDutchToIntlDate,
} from "shared/utils/dateHelpers";
import { answerOptionToDto } from "./answerOption.mapper";

/**
 * Ids of selected students that have no subject. A per-subject meeting has
 * nothing to capture for them, so the create/edit page blocks the submit and
 * names them (see `useStudentsWithoutSubjectsGuard`) rather than sending an
 * invalid payload.
 */
export const studentIdsWithoutSubjects = (
  schedules: Record<string, string[]>,
): string[] =>
  Object.entries(schedules)
    .filter(([, subjects]) => subjects.length === 0)
    .map(([id]) => id);

const composeDateTime = (date: string | null, time: string): Date | null => {
  if (!date) {
    return null;
  }
  return fromDutchToIntlDate(date, time) ?? null;
};

/**
 * Map the page-level form state to the snake_case DTO the API expects.
 * `studentIds` is intentionally not sent — the server derives selection from
 * the `schedules` keys.
 */
export const meetingFormDataToDto = (
  form: TransitionMeetingFormData,
): StoreTransitionMeetingDto => {
  const opensAt =
    form.opensAtMode === "immediately"
      ? new Date()
      : composeDateTime(form.opensAtDate, form.opensAtTime);

  const closedModifyAt = composeDateTime(
    form.closedModifyDate,
    form.closedModifyTime,
  );

  const closedAt =
    form.closedAtMode === "one_week"
      ? closedModifyAt
        ? addOneWeekToDate(closedModifyAt)
        : null
      : composeDateTime(form.closedAtDate, form.closedAtTime);

  return {
    name: form.name,
    message: form.message,
    question_prompt: form.questionPrompt || null,
    answer_type: form.answerType,
    answer_options: form.answerOptions.map(answerOptionToDto),
    schedules: form.schedules,
    timeframe_settings: {
      opens_at: opensAt?.toISOString() ?? "",
      closed_modify_at: closedModifyAt?.toISOString() ?? "",
      closed_at: closedAt?.toISOString() ?? "",
    },
  };
};
